import { z } from "zod";
import { AgencyIdSchema, ProjectIdSchema, RunIdSchema } from "../primitives/ids.js";
import { LaunchProofAggregateStatusSchema } from "./launch-proof-report.js";
export const LaunchCertificateIdSchema = z
    .string()
    .regex(/^lcert_[a-zA-Z0-9_-]+$/)
    .brand();
export const LaunchCertificateVerificationCodeSchema = z
    .string()
    .regex(/^lcert_[a-f0-9]{32}$/)
    .brand();
export const LaunchCertificateStatusSchema = z.enum(["ISSUED", "INELIGIBLE"]);
export const LaunchCertificateIneligibilityReasonSchema = z.enum([
    "launch_proof_blocked",
    "launch_proof_not_evaluated",
    "launch_proof_not_ready",
]);
export const LaunchCertificateEvidenceSectionV1Schema = z
    .object({
    schemaVersion: z.literal("launch-certificate-evidence-section.v1.0"),
    areaLabel: z.string().min(1),
    statusLabel: z.string().min(1),
    summary: z.string().min(1),
})
    .strict();
/** Worker-persisted Launch Certificate artifact (stored JSON). */
export const LaunchCertificateArtifactV1Schema = z
    .object({
    schemaVersion: z.literal("launch-certificate-artifact.v1.0"),
    certificateId: LaunchCertificateIdSchema,
    verificationCode: LaunchCertificateVerificationCodeSchema,
    status: z.literal("ISSUED"),
    businessName: z.string().min(1),
    agencyDisplayName: z.string().min(1),
    proofReferenceLabel: z.string().min(1),
    aggregateStatus: LaunchProofAggregateStatusSchema,
    aggregateStatusLabel: z.string().min(1),
    headline: z.string().min(1),
    evidenceSections: z.array(LaunchCertificateEvidenceSectionV1Schema).min(1),
    warningBanner: z.string().min(1).nullable(),
    issuedAt: z.string().datetime(),
    disclaimers: z.array(z.string().min(1)).min(1),
})
    .strict();
/** Client-safe certificate view — portal and printable HTML source. */
export const ClientSafeLaunchCertificateViewV1Schema = z
    .object({
    schemaVersion: z.literal("client-safe-launch-certificate-view.v1.0"),
    title: z.string().min(1),
    businessName: z.string().min(1),
    agencyDisplayName: z.string().min(1),
    proofReferenceLabel: z.string().min(1),
    aggregateStatusLabel: z.string().min(1),
    headline: z.string().min(1),
    evidenceSections: z.array(LaunchCertificateEvidenceSectionV1Schema).min(1),
    warningBanner: z.string().min(1).nullable(),
    issuedAt: z.string().datetime(),
    disclaimers: z.array(z.string().min(1)).min(1),
    clientSafe: z.literal(true),
})
    .strict();
/** Agency Studio view — includes verification code for sharing; no internal IDs. */
export const AgencyLaunchCertificateViewV1Schema = z
    .object({
    schemaVersion: z.literal("agency-launch-certificate-view.v1.0"),
    status: LaunchCertificateStatusSchema,
    ineligibilityReason: LaunchCertificateIneligibilityReasonSchema.nullable(),
    verificationCode: LaunchCertificateVerificationCodeSchema.nullable(),
    certificate: ClientSafeLaunchCertificateViewV1Schema.nullable(),
    publicVerifyPath: z.string().min(1).nullable(),
})
    .strict();
/** Public verification lookup — safe fields only. */
export const PublicLaunchCertificateVerificationV1Schema = z
    .object({
    schemaVersion: z.literal("public-launch-certificate-verification.v1.0"),
    verificationCode: LaunchCertificateVerificationCodeSchema,
    businessName: z.string().min(1),
    agencyDisplayName: z.string().min(1),
    proofReferenceLabel: z.string().min(1),
    aggregateStatusLabel: z.string().min(1),
    headline: z.string().min(1),
    evidenceSections: z.array(LaunchCertificateEvidenceSectionV1Schema).min(1),
    warningBanner: z.string().min(1).nullable(),
    issuedAt: z.string().datetime(),
    disclaimers: z.array(z.string().min(1)).min(1),
})
    .strict();
/** Internal compose input — worker only. */
export const LaunchCertificateComposeInputV1Schema = z
    .object({
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    runId: RunIdSchema,
    businessName: z.string().min(1),
    agencyDisplayName: z.string().min(1),
    proofReferenceLabel: z.string().min(1),
    aggregateStatus: LaunchProofAggregateStatusSchema,
    headline: z.string().min(1),
    evidenceSections: z.array(LaunchCertificateEvidenceSectionV1Schema).min(1),
    warningBanner: z.string().min(1).nullable(),
    issuedAt: z.string().datetime(),
})
    .strict();
//# sourceMappingURL=launch-certificate.js.map